import { useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '@core/utils/cn';

import { Skeleton } from './Skeleton';

export interface Column<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Right-align numeric columns; they read wrong ragged. */
  numeric?: boolean;
  className?: string;
}

interface DataTableProps<T> {
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  /** Shown in place of the body when there are no rows and nothing is loading. */
  empty?: ReactNode;
  /** Required: it names the scroll region as well as captioning the table. */
  caption: string;
}

/**
 * A table.
 *
 * <p>Hand-rolled rather than TanStack Table, and that is a deliberate scope call: sorting,
 * filtering and paging are all done by the server here (`PageRequestParams` validates the
 * sort against a per-resource allow-list, because a raw sort handed to `Pageable` lets a
 * caller probe the schema). A client-side table library would bring a second, competing
 * implementation of all three and be used for none of them.
 *
 * <p>Real semantic table markup, not a grid of divs: row and column relationships are what
 * make a table navigable to a screen reader, and they cannot be added back afterwards.
 *
 * <p>There is deliberately no `onRowClick`. A clickable `<tr>` cannot be reached by
 * keyboard, and the usual patches for that -- `tabIndex` plus `role="button"` on the row --
 * trade the table semantics above for the affordance, which is a bad swap. Row actions are
 * real buttons in an actions column, where they are focusable and have accessible names.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  empty,
  caption,
}: DataTableProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  /**
   * A horizontally scrolling region must be operable from the keyboard (WCAG 2.1.1).
   * Chrome now focuses scroll containers natively; Safari and Firefox do not, so the
   * container takes an explicit tab stop -- but only while it actually overflows, or
   * every table on a wide screen adds a tab stop that goes nowhere.
   *
   * Both boxes are observed: the container changes with the viewport, the table changes
   * with its own content (a relative time growing from "5 min" to "3 h 32 min" widens a
   * column without any row being added or removed).
   */
  const [scrollable, setScrollable] = useState(false);

  useEffect(() => {
    const container = scrollRef.current;
    const table = tableRef.current;
    if (!container || !table) return;

    const measure = () => setScrollable(container.scrollWidth > container.clientWidth + 1);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(table);
    return () => observer.disconnect();
    // `loading` is in the list because the table is not in the DOM while it is true, so
    // the mount-time run has nothing to observe and must happen again once it clears.
  }, [loading, rows.length, columns.length]);

  if (loading) {
    return (
      <div className="space-y-2 p-3">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-11 rounded-md" />
        ))}
      </div>
    );
  }

  if (rows.length === 0 && empty) {
    return <>{empty}</>;
  }

  return (
    <div
      ref={scrollRef}
      className="focus-visible:outline-accent overflow-x-auto outline-none focus-visible:outline-2 focus-visible:-outline-offset-2"
      {...(scrollable
        ? { tabIndex: 0, role: 'region', 'aria-label': `${caption} (scrollable)` }
        : {})}
    >
      <table ref={tableRef} className="w-full border-collapse text-[14px]">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-separator border-b">
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                className={cn(
                  'text-fg-subtle px-3 py-2 text-[11px] font-semibold tracking-[0.04em] whitespace-nowrap uppercase',
                  column.numeric ? 'text-right' : 'text-left',
                  column.className,
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-separator divide-y">
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((column) => (
                <td
                  key={column.id}
                  className={cn(
                    // `whitespace-nowrap` rather than letting cells wrap: at 360px a
                    // wrapping cell turns "3 h 32 min ago" into a four-line stack and the
                    // row grows to 90px of mostly whitespace. The table is already inside
                    // a scroll container, so keeping cells on one line is what makes that
                    // container do its job instead of squashing the content into it.
                    'px-3 py-2.5 align-middle whitespace-nowrap',
                    // Tabular numerals on every numeric cell: a column whose digits shift
                    // width as it updates is the fastest way to look cheap.
                    column.numeric && 'tnum text-right',
                    column.className,
                  )}
                >
                  {column.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
